import inventoryText from "../inventoryI18n.js";
import React from "react";
import axios from "axios";
import { useHistory } from "react-router-dom";
import { Modal } from "react-bootstrap";
import {
  DataGrid,
  GridToolbarContainer,
  GridToolbarColumnsButton,
  GridToolbarFilterButton,
  GridToolbarExport,
} from "@mui/x-data-grid";
import "bootstrap/dist/css/bootstrap.min.css";
import { API_URL } from "../../components/api";
import "./SiteOrders.css";

const getToken = () => {
  try {
    const tokens = JSON.parse(
      localStorage.getItem("authTokens") || "null"
    );
    return tokens?.access || "";
  } catch (error) {
    return "";
  }
};

const requestConfig = () => ({
  headers: {
    Authorization: `Bearer ${getToken()}`,
    "Content-Type": "application/json",
  },
});

const getError = (error, fallback) =>
  error?.response?.data?.message ||
  error?.response?.data?.error ||
  fallback;

const numberValue = (value) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : 0;
};

const money = (value) => numberValue(value).toFixed(2);

const firstValue = (object, keys, fallback = null) => {
  for (const key of keys) {
    if (
      object &&
      object[key] !== undefined &&
      object[key] !== null
    ) {
      return object[key];
    }
  }

  return fallback;
};

const getItems = (order) => {
  const items = firstValue(
    order,
    ["items", "order_items"],
    []
  );

  return Array.isArray(items) ? items : [];
};

const getItemPrice = (item) =>
  numberValue(
    firstValue(
      item,
      [
        "reference_unit_price_usd",
        "unit_reference_price_usd",
        "unit_price_usd",
        "unit_price",
      ],
      0
    )
  );

const getItemTotal = (item) => {
  const recorded = firstValue(
    item,
    [
      "reference_total_usd",
      "reference_line_total_usd",
      "line_total_usd",
      "total_price_usd",
      "total_price",
    ]
  );

  if (recorded !== null) {
    return numberValue(recorded);
  }

  return getItemPrice(item) * numberValue(item.quantity);
};

const getOrderTotal = (order) => {
  const recorded = firstValue(
    order,
    [
      "reference_total_usd",
      "total_reference_value_usd",
      "total_price_usd",
    ]
  );

  if (recorded !== null) {
    return numberValue(recorded);
  }

  return getItems(order).reduce(
    (sum, item) => sum + getItemTotal(item),
    0
  );
};

const getAccountHolderName = (order) =>
  firstValue(
    order,
    [
      "account_holder_name",
      "account_holder_name_en",
      "name_en",
    ],
    "-"
  );

const formatDate = (value) => {
  if (!value) {
    return "-";
  }

  const date = new Date(value);

  return Number.isNaN(date.getTime())
    ? String(value)
    : date.toLocaleString();
};

export function SiteOrdersDetails() {
  const [orders, setOrders] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const [search, setSearch] = React.useState("");
  const [error, setError] = React.useState("");
  const [success, setSuccess] = React.useState("");
  const [selectedOrder, setSelectedOrder] =
    React.useState(null);
  const [cancellingId, setCancellingId] =
    React.useState(null);

  const loadOrders = React.useCallback(async () => {
    setLoading(true);
    setError("");

    try {
      const response = await axios.get(
        `${API_URL}/inventory/site-orders/`,
        requestConfig()
      );

      const rows = Array.isArray(response.data)
        ? response.data
        : response.data?.orders || [];

      setOrders(rows);
    } catch (requestError) {
      setError(
        getError(
          requestError,
          "Site Orders could not be loaded."
        )
      );
    } finally {
      setLoading(false);
    }
  }, []);

  React.useEffect(() => {
    loadOrders();
  }, [loadOrders]);

  const filteredOrders = React.useMemo(() => {
    const query = search.trim().toLowerCase();

    if (!query) {
      return orders;
    }

    return orders.filter((order) => {
      const productNames = getItems(order)
        .map((item) =>
          firstValue(
            item,
            ["product_name", "name"],
            ""
          )
        )
        .join(" ");

      return [
        order.order_id,
        order.order_number,
        order.site_name,
        order.warehouse_name,
        getAccountHolderName(order),
        order.status,
        order.initiator,
        productNames,
      ]
        .join(" ")
        .toLowerCase()
        .includes(query);
    });
  }, [orders, search]);

  const cancelOrder = async (order) => {


    setError("");
    setSuccess("");
    setCancellingId(order.order_id);

    try {
      const response = await axios.post(
        `${API_URL}/inventory/site-orders/` +
          `${order.order_id}/cancel/`,
        {
          reason: "Cancelled from the Site Orders page.",
        },
        requestConfig()
      );

      setSuccess(
        response.data?.message ||
          "Site Order cancelled."
      );
      setSelectedOrder(null);
      await loadOrders();
    } catch (requestError) {
      setError(
        getError(
          requestError,
          "The Site Order could not be cancelled."
        )
      );
    } finally {
      setCancellingId(null);
    }
  };

  const SiteOrdersToolbar = () => (
    <GridToolbarContainer>
      <GridToolbarColumnsButton />
      <GridToolbarFilterButton />
      <GridToolbarExport />
    </GridToolbarContainer>
  );

  const orderColumns = [
    {
      field: "order_number",
      headerName: inventoryText("inventory.Order"),
      flex: 2,
      minWidth: 140,
      valueGetter: (params) =>
        params.row.order_number ||
        `SITE-${params.row.order_id}`,
    },
    {
      field: "site_name",
      headerName: inventoryText("inventory.Site"),
      flex: 2,
      minWidth: 110,
    },
    {
      field: "core_code",
      headerName: inventoryText("inventory.Core"),
      flex: 1,
      minWidth: 90,
    },
    {
      field: "warehouse_name",
      headerName: inventoryText("inventory.Warehouse"),
      flex: 2,
      minWidth: 130,
    },
    {
      field: "account_holder_name",
      headerName: inventoryText("inventory.AccountHolder"),
      flex: 2,
      minWidth: 140,
      valueGetter: (params) =>
        getAccountHolderName(params.row),
    },
    {
      field: "reference_total",
      headerName: inventoryText("inventory.ReferenceTotal"),
      flex: 1,
      minWidth: 120,
      valueGetter: (params) =>
        Number(getOrderTotal(params.row)),
      valueFormatter: (params) =>
        `$${money(params.value)}`,
    },
    {
      field: "date",
      headerName: inventoryText("inventory.Date"),
      flex: 2,
      minWidth: 170,
      valueFormatter: (params) =>
        formatDate(params.value),
    },
    {
      field: "initiator",
      headerName: inventoryText("inventory.Initiator"),
      flex: 1,
      minWidth: 100,
    },
    {
      field: "status",
      headerName: inventoryText("inventory.Status"),
      flex: 1,
      minWidth: 110,
    },
    {
      field: "actions",
      headerName: inventoryText("inventory.Actions"),
      flex: 2,
      minWidth: 190,
      sortable: false,
      filterable: false,
      renderCell: (params) => (
        <div>
          <button
            type="button"
            className="edit-client"
            onClick={() =>
              setSelectedOrder(params.row)
            }
          >
            {inventoryText("inventory.Show")}
          </button>

          {String(
            params.row.status
          ).toLowerCase() !== "cancelled" && (
            <button
              type="button"
              className="edit-client"
              disabled={
                cancellingId === params.row.order_id
              }
              onClick={() =>
                cancelOrder(params.row)
              }
            >
              {cancellingId === params.row.order_id
                ? "Cancelling..."
                : "Cancel"}
            </button>
          )}
        </div>
      ),
    },
  ];

  return (
    <div className="site-orders-page">
      <div className="site-orders-panel">
        <div className="site-orders-header">
          <button
            type="button"
            className="edit-client site-orders-form-button"
            onClick={loadOrders}
            disabled={loading}
          >{inventoryText("inventory.Refresh")}</button>
        </div>

        {success && (
          <div className="site-orders-alert success">
            {success}
          </div>
        )}

        {error && (
          <div className="site-orders-alert error">
            {error}
          </div>
        )}

        <input
          type="text"
          className="search-client"
          placeholder={inventoryText("inventory.SearchSiteOrders")}
          value={search}
          onChange={(event) =>
            setSearch(event.target.value)
          }
        />

        {loading && (
          <div className="site-orders-empty">
            {inventoryText("inventory.LoadingSiteOrders")}
          </div>
        )}

        {!loading && filteredOrders.length === 0 && (
          <div className="site-orders-empty">
            {inventoryText("inventory.NoSiteOrdersFound")}
          </div>
        )}

        {!loading && filteredOrders.length > 0 && (
          <div
            style={{ height: 520, width: "100%" }}
            className="table-container"
          >
            <DataGrid
              rows={filteredOrders}
              columns={orderColumns}
              getRowId={(row) => row.order_id}
              components={{ Toolbar: SiteOrdersToolbar }}
              rowsPerPageOptions={[10, 100, 1000]}
              pagination
              disableSelectionOnClick
            />
          </div>
        )}
      </div>

      <Modal
        className="site-orders-modal"
        show={Boolean(selectedOrder)}
        onHide={() => setSelectedOrder(null)}
        centered
        size="lg"
      >
        <Modal.Header closeButton>
          <Modal.Title>{inventoryText("inventory.SiteOrderDetails")}</Modal.Title>
        </Modal.Header>

        <Modal.Body>
          {selectedOrder && (
            <>
              <div className="site-orders-modal-summary">
                <div>
                  <strong>Order:</strong>{" "}
                  {selectedOrder.order_number}
                </div>
                <div>
                  <strong>Status:</strong>{" "}
                  {selectedOrder.status}
                </div>
                <div>
                  <strong>Site:</strong>{" "}
                  {selectedOrder.site_name || "-"}
                </div>
                <div>
                  <strong>Core:</strong>{" "}
                  {selectedOrder.core_code || "-"}
                </div>
                <div>
                  <strong>Warehouse:</strong>{" "}
                  {selectedOrder.warehouse_name || "-"}
                </div>
                <div>
                  <strong>Account Holder:</strong>{" "}
                  {getAccountHolderName(selectedOrder)}
                </div>
                <div>
                  <strong>Reference Total:</strong> $
                  {money(getOrderTotal(selectedOrder))}
                </div>
                <div>
                  <strong>Initiator:</strong>{" "}
                  {selectedOrder.initiator || "-"}
                </div>
                <div>
                  <strong>Date:</strong>{" "}
                  {formatDate(selectedOrder.date)}
                </div>
              </div>

              <div className="site-orders-table-wrap">
                <table className="site-orders-modal-table">
                  <thead>
                    <tr>
                      <th>{inventoryText("inventory.Product")}</th>
                      <th>{inventoryText("inventory.Code")}</th>
                      <th>{inventoryText("inventory.Quantity")}</th>
                      <th>{inventoryText("inventory.ReferenceUnitPrice")}</th>
                      <th>{inventoryText("inventory.ReferenceTotal")}</th>
                    </tr>
                  </thead>

                  <tbody>
                    {getItems(selectedOrder).map(
                      (item, index) => (
                        <tr
                          key={
                            item.entry_id ||
                            item.order_item_id ||
                            index
                          }
                        >
                          <td>
                            {firstValue(
                              item,
                              ["product_name", "name"],
                              "-"
                            )}
                          </td>
                          <td>
                            {firstValue(
                              item,
                              ["product_code", "code"],
                              "-"
                            )}
                          </td>
                          <td>{item.quantity}</td>
                          <td>
                            ${money(getItemPrice(item))}
                          </td>
                          <td>
                            ${money(getItemTotal(item))}
                          </td>
                        </tr>
                      )
                    )}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </Modal.Body>

        <Modal.Footer>
          {selectedOrder &&
            String(
              selectedOrder.status
            ).toLowerCase() !== "cancelled" && (
              <button
                type="button"
                className="edit-client site-orders-row-button"
                disabled={
                  cancellingId ===
                  selectedOrder.order_id
                }
                onClick={() =>
                  cancelOrder(selectedOrder)
                }
              >{inventoryText("inventory.CancelAndRestoreStock")}</button>
            )}

          <button
            type="button"
            className="edit-client site-orders-row-button"
            onClick={() => setSelectedOrder(null)}
          >{inventoryText("inventory.Close")}</button>
        </Modal.Footer>
      </Modal>
    </div>
  );
}

export function SiteOrderForm() {
  const history = useHistory();

  const [options, setOptions] = React.useState({
    sites: [],
    warehouses: [],
    account_holders: [],
    cores: [],
  });
  const [products, setProducts] = React.useState([]);
  const [siteId, setSiteId] = React.useState("");
  const [coreId, setCoreId] = React.useState("");
  const [warehouseId, setWarehouseId] =
    React.useState("");
  const [accountHolderId, setAccountHolderId] =
    React.useState("");
  const [productId, setProductId] =
    React.useState("");
  const [quantity, setQuantity] = React.useState(1);
  const [items, setItems] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const [loadingProducts, setLoadingProducts] =
    React.useState(false);
  const [submitting, setSubmitting] =
    React.useState(false);
  const [error, setError] = React.useState("");
  const [success, setSuccess] = React.useState("");


  React.useEffect(() => {
    if (!success) return undefined;

    const timeoutId = window.setTimeout(
      () => setSuccess(""),
      4000
    );

    return () =>
      window.clearTimeout(timeoutId);
  }, [success]);

  React.useEffect(() => {
    let mounted = true;

    const loadOptions = async () => {
      try {
        const response = await axios.get(
          `${API_URL}/inventory/site-orders/options/`,
          requestConfig()
        );

        if (mounted) {
          setOptions({
            sites: response.data?.sites || [],
            cores: response.data?.cores || [],
            warehouses:
              response.data?.warehouses || [],
            account_holders:
              response.data?.account_holders || [],
          });
        }
      } catch (requestError) {
        if (mounted) {
          setError(
            getError(
              requestError,
              "Site Order options could not be loaded."
            )
          );
        }
      } finally {
        if (mounted) {
          setLoading(false);
        }
      }
    };

    loadOptions();

    return () => {
      mounted = false;
    };
  }, []);

  const loadProducts = React.useCallback(
    async (selectedWarehouseId) => {
      if (!selectedWarehouseId) {
        setProducts([]);
        return;
      }

      setLoadingProducts(true);
      setError("");

      try {
        const response = await axios.get(
          `${API_URL}/inventory/site-orders/options/`,
          {
            ...requestConfig(),
            params: {
              warehouse_id:
                selectedWarehouseId,
            },
          }
        );

        setProducts(
          response.data?.products || []
        );
      } catch (requestError) {
        setProducts([]);
        setError(
          getError(
            requestError,
            "Warehouse products could not be loaded."
          )
        );
      } finally {
        setLoadingProducts(false);
      }
    },
    []
  );

  React.useEffect(() => {
    setItems([]);
    setProductId("");
    setQuantity(1);
    loadProducts(warehouseId);
  }, [warehouseId, loadProducts]);

  const selectedProduct = products.find(
    (product) =>
      String(product.product_id) ===
      String(productId)
  );

  const addProduct = () => {
    setError("");
    setSuccess("");

    if (!selectedProduct) {
      setError("Select a product.");
      return;
    }

    const requestedQuantity = Number(quantity);

    if (
      !Number.isInteger(requestedQuantity) ||
      requestedQuantity <= 0
    ) {
      setError(
        "Quantity must be a positive whole number."
      );
      return;
    }

    if (
      requestedQuantity >
      Number(selectedProduct.available_quantity)
    ) {
      setError(
        `Only ${selectedProduct.available_quantity} ` +
          "unit(s) are available."
      );
      return;
    }

    if (
      items.some(
        (item) =>
          String(item.product_id) ===
          String(selectedProduct.product_id)
      )
    ) {
      setError(
        "This product is already in the order."
      );
      return;
    }

    setItems((current) => [
      ...current,
      {
        ...selectedProduct,
        quantity: requestedQuantity,
      },
    ]);
    setProductId("");
    setQuantity(1);
  };

  const referenceTotal = items.reduce(
    (sum, item) =>
      sum +
      numberValue(
        item.reference_unit_price_usd
      ) *
        numberValue(item.quantity),
    0
  );

  const submitOrder = async () => {
    setError("");
    setSuccess("");

    if (
      !warehouseId ||
      !accountHolderId ||
      items.length === 0
    ) {
      setError(
        "Select a warehouse, account holder, " +
          "and at least one product."
      );
      return;
    }

    if (Boolean(siteId) === Boolean(coreId)) {
      setError(inventoryText("inventory.SiteOrCoreRequired"));
      return;
    }

    setSubmitting(true);

    try {
      const response = await axios.post(
        `${API_URL}/inventory/site-orders/`,
        {
          site_id: siteId ? Number(siteId) : null,
          core_id: coreId ? Number(coreId) : null,
          warehouse_id: Number(warehouseId),
          account_holder_id:
            Number(accountHolderId),
          items: items.map((item) => ({
            product_id: Number(item.product_id),
            quantity: Number(item.quantity),
          })),
        },
        requestConfig()
      );

      setSuccess(
        `${response.data?.order_number ||
          "Site Order"} was confirmed and ` +
          "warehouse stock was updated."
      );
      setSiteId("");
      setCoreId("");
      setAccountHolderId("");
      setWarehouseId("");
      setProducts([]);
      setItems([]);
      setProductId("");
      setQuantity(1);
    } catch (requestError) {
      setError(
        getError(
          requestError,
          "The Site Order could not be confirmed."
        )
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="site-orders-page">
      <div className="site-orders-panel">
        <div className="site-orders-header">
          <div>
            <h2>{inventoryText("inventory.AddSiteOrder")}</h2>
          </div>

          <button
            type="button"
            className="edit-client site-orders-form-button"
            onClick={() =>
              history.push(
                "/inventory/site-orders"
              )
            }
          >{inventoryText("inventory.ViewSiteOrders")}</button>
        </div>

        {success && (
          <div className="site-orders-alert success">
            {success}
          </div>
        )}

        {error && (
          <div className="site-orders-alert error">
            {error}
          </div>
        )}

        <div className="site-orders-form-grid">
          <div className="site-orders-field">
            <label htmlFor="site-order-site">{inventoryText("inventory.Site")}</label>
            <select
              id="site-order-site"
              value={siteId}
              disabled={loading || Boolean(coreId)}
              onChange={(event) => {
                setSiteId(event.target.value);
                if (event.target.value) {
                  setCoreId("");
                }
              }}
            >
              <option value="">{inventoryText("inventory.SelectSite")}</option>
              {options.sites.map((site) => (
                <option
                  key={site.site_id}
                  value={site.site_id}
                >
                  {site.site_name}
                </option>
              ))}
            </select>
          </div>

          <div className="site-orders-field">
            <label htmlFor="site-order-core">
              {inventoryText("inventory.Core")}
            </label>
            <select
              id="site-order-core"
              value={coreId}
              disabled={loading || Boolean(siteId)}
              onChange={(event) => {
                setCoreId(event.target.value);
                if (event.target.value) {
                  setSiteId("");
                }
              }}
            >
              <option value="">
                {inventoryText("inventory.SelectCore")}
              </option>
              {options.cores.map((core) => (
                <option
                  key={core.core_id}
                  value={core.core_id}
                >
                  {core.code}
                </option>
              ))}
            </select>
          </div>

          <div className="site-orders-field">
            <label htmlFor="site-order-holder">{inventoryText("inventory.AccountHolder")}</label>
            <select
              id="site-order-holder"
              value={accountHolderId}
              disabled={loading}
              onChange={(event) =>
                setAccountHolderId(
                  event.target.value
                )
              }
            >
              <option value="">{inventoryText("inventory.SelectAccountHolder")}</option>
              {options.account_holders.map(
                (holder) => (
                  <option
                    key={holder.account_holder_id}
                    value={holder.account_holder_id}
                  >
                    {holder.name_en ||
                      holder.name_ar ||
                      `Holder ${
                        holder.account_holder_id
                      }`}
                  </option>
                )
              )}
            </select>
          </div>

          <div className="site-orders-field">
            <label htmlFor="site-order-warehouse">{inventoryText("inventory.Warehouse")}</label>
            <select
              id="site-order-warehouse"
              value={warehouseId}
              disabled={loading}
              onChange={(event) =>
                setWarehouseId(
                  event.target.value
                )
              }
            >
              <option value="">{inventoryText("inventory.SelectWarehouse")}</option>
              {options.warehouses.map(
                (warehouse) => (
                  <option
                    key={warehouse.warehouse_id}
                    value={warehouse.warehouse_id}
                  >
                    {warehouse.warehouse_name}
                  </option>
                )
              )}
            </select>
          </div>
        </div>

        <div className="site-orders-product-box">
          <div className="site-orders-product-grid">
            <div className="site-orders-field">
              <label htmlFor="site-order-product">{inventoryText("inventory.Product")}</label>
              <select
                id="site-order-product"
                value={productId}
                disabled={
                  !warehouseId || loadingProducts
                }
                onChange={(event) =>
                  setProductId(event.target.value)
                }
              >
                <option value="">
                  {loadingProducts
                    ? "Loading products..."
                    : "Select product..."}
                </option>
                {products.map((product) => (
                  <option
                    key={product.product_id}
                    value={product.product_id}
                  >
                    {product.product_name}
                    {product.product_code
                      ? ` (${product.product_code})`
                      : ""}
                  </option>
                ))}
              </select>
            </div>

            <div className="site-orders-field">
              <label htmlFor="site-order-quantity">{inventoryText("inventory.Quantity")}</label>
              <input
                id="site-order-quantity"
                type="number"
                min="1"
                max={
                  selectedProduct
                    ?.available_quantity || ""
                }
                value={quantity}
                onChange={(event) =>
                  setQuantity(event.target.value)
                }
              />
            </div>

            <div className="site-orders-field">
              <label>{inventoryText("inventory.Available")}</label>
              <div className="site-orders-value">
                {selectedProduct
                  ? selectedProduct
                      .available_quantity
                  : "-"}
              </div>
            </div>

            <div className="site-orders-field">
              <label>{inventoryText("inventory.ReferencePrice")}</label>
              <div className="site-orders-value">
                {selectedProduct
                  ? `$${money(
                      selectedProduct
                        .reference_unit_price_usd
                    )}`
                  : "-"}
              </div>
            </div>

            <button
              type="button"
              className="edit-client site-orders-form-button"
              disabled={!selectedProduct}
              onClick={addProduct}
            >{inventoryText("inventory.AddProduct")}</button>
          </div>
        </div>

        <div className="site-orders-table-wrap">
          <table className="site-orders-table">
            <thead>
              <tr>
                <th>{inventoryText("inventory.Product")}</th>
                <th>{inventoryText("inventory.Code")}</th>
                <th>{inventoryText("inventory.Quantity")}</th>
                <th>{inventoryText("inventory.Available")}</th>
                <th>{inventoryText("inventory.ReferenceUnitPrice")}</th>
                <th>{inventoryText("inventory.ReferenceTotal")}</th>
                <th>{inventoryText("inventory.Action")}</th>
              </tr>
            </thead>

            <tbody>
              {items.map((item) => (
                <tr key={item.product_id}>
                  <td>{item.product_name}</td>
                  <td>{item.product_code || "-"}</td>
                  <td>{item.quantity}</td>
                  <td>{item.available_quantity}</td>
                  <td>
                    $
                    {money(
                      item.reference_unit_price_usd
                    )}
                  </td>
                  <td>
                    $
                    {money(
                      numberValue(
                        item.reference_unit_price_usd
                      ) *
                        numberValue(item.quantity)
                    )}
                  </td>
                  <td>
                    <button
                      type="button"
                      className="edit-client site-orders-row-button"
                      onClick={() =>
                        setItems((current) =>
                          current.filter(
                            (existing) =>
                              existing.product_id !==
                              item.product_id
                          )
                        )
                      }
                    >{inventoryText("inventory.Remove")}</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          {items.length === 0 && (
            <div className="site-orders-empty">{inventoryText("inventory.NoProductsHaveBeenAdded")}</div>
          )}
        </div>

        <div className="site-orders-footer">
          <div className="site-orders-total">
            Reference Total: $
            {money(referenceTotal)}
          </div>

          <button
            type="button"
            className="edit-client site-orders-form-button"
            disabled={
              submitting ||
              !warehouseId ||
              !accountHolderId ||
              items.length === 0
            }
            onClick={submitOrder}
          >
            {submitting
              ? "Confirming..."
              : "Confirm Site Order"}
          </button>
        </div>
      </div>
    </div>
  );
}
